import React, { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { CheckCircle, Spinner, X, ArrowSquareOut, ArrowsClockwise, CaretDown } from '@phosphor-icons/react';
import { toast } from 'sonner';

// Telegram bot username comes from the env file selected at build time.
// Configure via .env.local / .env.firebase: REACT_APP_TELEGRAM_BOT=YourBotName
const BOT_USERNAME = process.env.REACT_APP_TELEGRAM_BOT;

// Each mounted instance gets its own global callback name so the Telegram
// widget can find it (it executes a string like "onTelegramAuthXXXX(user)").
function uniqueCallbackName() {
  return `onTelegramAuth_${Math.random().toString(36).slice(2, 10)}`;
}

export default function TelegramConnect() {
  const { user, refreshMe } = useAuth();
  const { t } = useI18n();
  const containerRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  // Already-connected = backend exposes telegram_chat_id (or telegram_connected) on the user.
  const initiallyConnected = Boolean(
    user?.telegram_chat_id ?? user?.telegram_connected ?? user?.telegram_id
  );
  const [connected, setConnected] = useState(initiallyConnected);
  const [reloadKey, setReloadKey] = useState(0);
  const [showSwitchHelp, setShowSwitchHelp] = useState(false);

  useEffect(() => {
    // Keep local state in sync if AuthContext refreshes.
    setConnected(initiallyConnected);
  }, [initiallyConnected]);

  useEffect(() => {
    if (connected) return;        // already connected — don't render widget
    if (!BOT_USERNAME) return;    // misconfigured — handled in render below
    const container = containerRef.current;
    if (!container) return;

    const cbName = uniqueCallbackName();

    // The Telegram widget calls this exact function name after a successful
    // login. It hands back: { id, first_name, last_name, username, photo_url,
    // auth_date, hash }. We MUST forward every field verbatim — Telegram's
    // hash is computed over ALL fields except `hash` itself (sorted, joined
    // with \n). Dropping or renaming any field makes the backend's hash
    // verification fail. The backend reads `id` to use as telegram_chat_id.
    window[cbName] = async (tgUser) => {
      // eslint-disable-next-line no-console
      console.log('[TelegramConnect] callback fired, payload =', tgUser);

      // Guard: refuse to post a body that the backend would interpret as
      // "id=0, empty strings" via Pydantic defaults. That happens when the
      // widget evals `data-onauth="cb(user)"` in a scope where `user` is
      // undefined — typically a misconfigured bot domain in BotFather.
      if (!tgUser || typeof tgUser !== 'object' || !tgUser.id || !tgUser.hash) {
        // eslint-disable-next-line no-console
        console.error('[TelegramConnect] received invalid payload from widget:', tgUser);
        toast.error(
          t('telegram_connect_error') +
          ' (widget returned empty user — check BotFather /setdomain matches this exact host)'
        );
        return;
      }

      // Explicit object so axios JSON.stringify sees every property even if
      // tgUser was something unusual like a Proxy or non-enumerable bag.
      const payload = {
        id: tgUser.id,
        first_name: tgUser.first_name,
        last_name: tgUser.last_name,
        username: tgUser.username,
        photo_url: tgUser.photo_url,
        auth_date: tgUser.auth_date,
        hash: tgUser.hash,
      };
      // Strip undefined keys — Telegram omits optional ones (no `last_name`
      // for users without one). Sending `last_name: undefined` would serialise
      // to `null` and corrupt the backend's hash check.
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
      // eslint-disable-next-line no-console
      console.log('[TelegramConnect] POSTing payload =', payload);

      try {
        setSubmitting(true);
        await api.post('/users/me/telegram/connect', payload);
        toast.success(t('telegram_connect_success'));
        setConnected(true);
        setShowSwitchHelp(false);
        // Refresh the user so AuthContext.user reflects the new connection state.
        await refreshMe();
      } catch (e) {
        const status = e.response?.status;
        const detail = e.response?.data?.detail;
        const detailLc = typeof detail === 'string' ? detail.toLowerCase() : '';
        // 409 (proper conflict) OR a generic 500 "database error" almost
        // always means the unique constraint on telegram_chat_id fired —
        // i.e. this Telegram account is already linked to another e2-care user.
        const looksLikeDuplicate =
          status === 409 ||
          detailLc.includes('duplicate') ||
          detailLc.includes('already') ||
          detailLc.includes('unique') ||
          detailLc.includes('database error');
        if (looksLikeDuplicate) {
          toast.error(t('telegram_already_linked'));
          setShowSwitchHelp(true);
        } else {
          toast.error(detail || t('telegram_connect_error'));
        }
      } finally {
        setSubmitting(false);
      }
    };

    // Build the <script> element with Telegram's required data-* attributes.
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.async = true;
    script.setAttribute('data-telegram-login', BOT_USERNAME);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-radius', '12');
    script.setAttribute('data-userpic', 'false');
    script.setAttribute('data-request-access', 'write');
    script.setAttribute('data-onauth', `${cbName}(user)`);

    container.appendChild(script);

    return () => {
      try { delete window[cbName]; } catch { window[cbName] = undefined; }
      if (container.contains(script)) container.removeChild(script);
      // The widget injects an iframe sibling of the <script> — sweep it too.
      container.querySelectorAll('iframe').forEach((f) => f.remove());
    };
  }, [connected, refreshMe, t, reloadKey]);

  // Build a display name for the connected Telegram account using the
  // fields the backend exposes. Falls back gracefully if some fields are
  // missing, and ultimately to the generic "Telegram connected" label so
  // we never render an empty badge.
  const tgFirst = user?.telegram_first_name;
  const tgLast = user?.telegram_last_name;
  const tgUsername = user?.telegram_username;
  const tgDisplay = [tgFirst, tgLast].filter(Boolean).join(' ').trim()
    || (tgUsername ? `@${tgUsername}` : null);

  const handleDisconnect = async () => {
    if (submitting) return;
    try {
      setSubmitting(true);
      await api.delete('/users/me/telegram');
      toast.success(t('telegram_disconnect_success'));
      setConnected(false);
      await refreshMe();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('telegram_disconnect_error'));
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Connected state ----
  if (connected) {
    return (
      <div
        data-testid="telegram-connected-row"
        className="flex flex-wrap items-center gap-2 max-w-full"
      >
        <span
          data-testid="telegram-connected-badge"
          className="inline-flex items-center gap-2 text-sm font-medium text-sage-700 bg-sage-100 px-3 py-1.5 rounded-full max-w-full min-w-0 overflow-hidden"
          title={tgDisplay ? t('telegram_connected_as', { name: tgDisplay }) : t('telegram_connected')}
        >
          <CheckCircle size={16} weight="fill" className="shrink-0" />
          <span className="truncate">
            {tgDisplay
              ? t('telegram_connected_as', { name: tgDisplay })
              : t('telegram_connected')}
          </span>
        </span>
        <button
          type="button"
          onClick={handleDisconnect}
          disabled={submitting}
          data-testid="telegram-disconnect-button"
          aria-label={t('telegram_disconnect')}
          title={t('telegram_disconnect')}
          className="inline-flex items-center gap-1 text-xs font-medium text-ink-soft hover:text-terracotta-700
                     border border-stone_line hover:border-terracotta-200 bg-white
                     rounded-full px-3 py-1.5 transition-colors disabled:opacity-60"
        >
          {submitting ? (
            <Spinner size={12} className="animate-spin" />
          ) : (
            <X size={12} weight="bold" />
          )}
          <span>{t('telegram_disconnect')}</span>
        </button>
      </div>
    );
  }

  // ---- Bot username not configured ----
  if (!BOT_USERNAME) {
    return (
      <span
        data-testid="telegram-misconfigured"
        className="text-xs text-terracotta-700 max-w-[260px] sm:text-right leading-snug"
      >
        {t('telegram_missing_bot')}
      </span>
    );
  }

  // ---- Default: render the widget ----
  return (
    <div className="flex flex-col items-start sm:items-end gap-2" data-testid="telegram-connect-widget">
      <div className="flex flex-wrap items-center gap-3">
        {submitting && (
          <span className="inline-flex items-center gap-2 text-xs text-ink-soft">
            <Spinner size={14} className="animate-spin" />
            {t('loading')}
          </span>
        )}
        <div ref={containerRef} />
      </div>

      <button
        type="button"
        onClick={() => setShowSwitchHelp((v) => !v)}
        data-testid="telegram-switch-toggle"
        className="inline-flex items-center gap-1 text-xs text-ink-soft hover:text-ink underline decoration-dotted"
      >
        {t('telegram_switch_q')}
        <CaretDown size={12} weight="bold" className={`transition-transform ${showSwitchHelp ? 'rotate-180' : ''}`} />
      </button>

      {showSwitchHelp && (
        <div
          data-testid="telegram-switch-help"
          className="w-full max-w-md text-xs text-ink-soft bg-bg-alt border border-stone_line rounded-xl p-3 space-y-2"
        >
          <p>{t('telegram_switch_intro')}</p>
          <ol className="list-decimal list-inside space-y-1 pl-1">
            <li>{t('telegram_switch_step1')}</li>
            <li>{t('telegram_switch_step2')}</li>
          </ol>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <a
              href="https://web.telegram.org/k/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="telegram-open-web"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-stone_line text-ink hover:bg-sage-100"
            >
              <ArrowSquareOut size={12} weight="bold" />
              {t('telegram_open_web')}
            </a>
            <button
              type="button"
              onClick={() => setReloadKey((k) => k + 1)}
              data-testid="telegram-reload-widget"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-sage text-white hover:bg-sage-700"
            >
              <ArrowsClockwise size={12} weight="bold" />
              {t('telegram_reload_widget')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
